import { POLICY_PRESETS } from "../preferences/default-presets";
import {
	type CommenterDefinition,
	DEFAULT_COMMENTER_DEFINITIONS,
} from "../types/core";

/**
 * Pre-calculated base map of all known commenter definitions.
 * This includes definitions from all presets and the defaults.
 * Pre-calculating this outside of React components avoids redundant
 * O(N*M) iterations during render loops or useMemo updates.
 */
const STATIC_COMMENTER_MAP = (() => {
	const map = new Map<string, CommenterDefinition>();

	// Lowest priority: All definitions across all policy presets
	for (const preset of POLICY_PRESETS) {
		for (const def of preset.commenterDefinitions) {
			map.set(def.id, def);
		}
	}

	// Medium priority: Default definitions
	for (const def of DEFAULT_COMMENTER_DEFINITIONS) {
		map.set(def.id, def);
	}

	return map;
})();

/**
 * Efficiently merges the static base map with active definitions.
 * @param activeDefinitions Definitions from current session or preferences
 * @returns A new Map for O(1) lookups
 */
export const getMergedCommenterMap = (
	activeDefinitions: CommenterDefinition[],
): Map<string, CommenterDefinition> => {
	// Performance optimization: Return static map reference if no active definitions are provided.
	// This avoids O(N) map copy and allocation in the common case where session-specific
	// definitions haven't been loaded yet.
	if (activeDefinitions.length === 0) {
		return STATIC_COMMENTER_MAP;
	}

	// Start with a shallow copy of the pre-calculated static map
	const map = new Map(STATIC_COMMENTER_MAP);

	// Highest priority: Active definitions from props
	for (const def of activeDefinitions) {
		map.set(def.id, def);
	}

	return map;
};
